import { Event, Rank, UnitType } from '@/types';

export type PrefixOption = { id: string; name: string; shortName: string };

export function normaliseCustomPrefixesForSave(prefixes: string[]): string[] {
  return [...new Set(prefixes.map((prefix) => prefix.trim()).filter(Boolean))];
}

export function hasConfiguredPrefixes(
  event: Pick<Event, 'useCustomPrefixes' | 'customPrefixes'> | null | undefined,
  unitType: UnitType | undefined,
): boolean {
  return getAvailablePrefixes(event, unitType).length > 0;
}

export type RsvpPrefixValidationResult =
  | { ok: true }
  | { ok: false; reason: 'no_configured_prefixes' | 'prefix_required' };

/** Mirrors RSVP affiliation prefix checks before submit. */
export function validateRsvpPrefixSelection(options: {
  requireAffiliationInfo: boolean;
  isApology: boolean;
  isInterestOnly: boolean;
  selectedRank: string;
  event: Pick<Event, 'useCustomPrefixes' | 'customPrefixes'> | null | undefined;
  unitType: UnitType | undefined;
}): RsvpPrefixValidationResult {
  const { requireAffiliationInfo, isApology, isInterestOnly, selectedRank, event, unitType } =
    options;

  if (isApology || isInterestOnly || !requireAffiliationInfo) {
    return { ok: true };
  }

  if (!hasConfiguredPrefixes(event, unitType)) {
    return { ok: false, reason: 'no_configured_prefixes' };
  }

  if (!selectedRank.trim()) {
    return { ok: false, reason: 'prefix_required' };
  }

  return { ok: true };
}

/** Mirrors RSVP document fields written from the selected prefix. */
export function buildRsvpPrefixFields(
  rankName: string,
  unitType: UnitType | undefined,
  globalRanks: Rank[],
): { rank?: string; rankShortName: string | null } {
  const { rank: resolvedRank, rankShortName } = getRankAndShortName({
    rankName,
    currentUnitType: unitType,
    globalRanks,
  });

  if (!resolvedRank) {
    return { rankShortName: null };
  }

  return {
    rank: resolvedRank,
    rankShortName,
  };
}

export function getAvailablePrefixes(
  event: Pick<Event, 'useCustomPrefixes' | 'customPrefixes'> | null | undefined,
  unitType: UnitType | undefined,
): PrefixOption[] {
  if (event?.useCustomPrefixes) {
    const custom = (event.customPrefixes || [])
      .map((prefix) => (typeof prefix === 'string' ? prefix.trim() : ''))
      .filter((prefix) => prefix !== '');

    return custom.map((name, index) => ({
      id: `custom-${index}`,
      name,
      shortName: '',
    }));
  }

  return (unitType?.ranks || [])
    .filter((rankItem) => rankItem.name && rankItem.name.trim() !== '')
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((rankItem) => ({
      id: rankItem.id,
      name: rankItem.name.trim(),
      shortName: rankItem.shortName?.trim() || '',
    }));
}

interface RankLookupOptions {
  rankName: string | null | undefined;
  currentUnitType?: UnitType;
  globalRanks?: Rank[];
}

const normaliseName = (value: string | null | undefined): string => {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = value.trim();

  if (trimmed === '') {
    return '';
  }

  return trimmed.toLowerCase();
};

const findRankInList = (ranks: Rank[] | undefined, rankName: string): Rank | undefined => {
  if (!ranks || ranks.length === 0) {
    return undefined;
  }

  const target = normaliseName(rankName);

  if (!target) {
    return undefined;
  }

  return ranks.find((rank) => {
    const byName = normaliseName(rank.name);
    const byShortName = normaliseName(rank.shortName);

    return byName === target || byShortName === target;
  });
};

export const getRankAndShortName = ({
  rankName,
  currentUnitType,
  globalRanks,
}: RankLookupOptions): { rank: string | null; rankShortName: string | null } => {
  const trimmedRank = typeof rankName === 'string' ? rankName.trim() : '';

  if (!trimmedRank) {
    return {
      rank: null,
      rankShortName: null,
    };
  }

  const unitRanks = currentUnitType?.ranks ?? [];

  const matchedRank =
    findRankInList(unitRanks, trimmedRank) ||
    findRankInList(globalRanks ?? [], trimmedRank);

  if (!matchedRank) {
    return {
      rank: trimmedRank,
      rankShortName: null,
    };
  }

  const canonicalName = typeof matchedRank.name === 'string' ? matchedRank.name.trim() : '';
  const shortName = typeof matchedRank.shortName === 'string' ? matchedRank.shortName.trim() : '';

  return {
    rank: canonicalName || trimmedRank,
    rankShortName: shortName || null,
  };
};

/** Prefix for guest lists: stored short form, or looked-up shortName from global ranks (never the long rank name). */
export function getGuestListRankPrefixShort(
  guest: { rank?: string | null; rankShortName?: string | null },
  globalRanks: Rank[]
): string {
  const stored = typeof guest.rankShortName === 'string' ? guest.rankShortName.trim() : '';
  if (stored) {
    return stored;
  }

  const rankName = typeof guest.rank === 'string' ? guest.rank.trim() : '';
  if (!rankName) {
    return '';
  }

  const { rankShortName } = getRankAndShortName({ rankName, globalRanks });
  return rankShortName?.trim() || '';
}

/** Guest list line: short rank prefix + name (no long-form prefix when no short can be resolved). */
export function formatGuestListDisplayName(
  guest: { name?: string | null; rank?: string | null; rankShortName?: string | null },
  globalRanks: Rank[]
): string {
  const prefix = getGuestListRankPrefixShort(guest, globalRanks);
  const name = typeof guest.name === 'string' ? guest.name.trim() : '';
  if (prefix && name) {
    return `${prefix} ${name}`;
  }
  return name;
}

